"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ALL_PLATFORM_PERMISSIONS = exports.PlatformPermissions = void 0;
/**
 * Permission string catalog for the Platform Engine (Stage 2).
 * Format: `<module>:<action>` per /standards/NamingConvention.md.
 * Single source of truth consumed by:
 *   - backend route guards (core/authorization's requirePermission)
 *   - the Permission Engine's seed data (each string here becomes a
 *     row in the `permissions` table)
 *   - a future frontend, to show/hide UI without duplicating the list
 *
 * Never construct a permission string inline at a call site — import
 * from here.
 */
exports.PlatformPermissions = {
    TENANT_CREATE: 'tenant:create',
    TENANT_READ: 'tenant:read',
    TENANT_UPDATE: 'tenant:update',
    TENANT_SUSPEND: 'tenant:suspend',
    TENANT_DELETE: 'tenant:delete',
    HOSPITAL_CREATE: 'hospital:create',
    HOSPITAL_READ: 'hospital:read',
    HOSPITAL_UPDATE: 'hospital:update',
    HOSPITAL_DELETE: 'hospital:delete',
    BRANCH_CREATE: 'branch:create',
    BRANCH_READ: 'branch:read',
    BRANCH_UPDATE: 'branch:update',
    BRANCH_DELETE: 'branch:delete',
    DEPARTMENT_CREATE: 'department:create',
    DEPARTMENT_READ: 'department:read',
    DEPARTMENT_UPDATE: 'department:update',
    DEPARTMENT_DELETE: 'department:delete',
    DEPARTMENT_EXPORT: 'department:export',
    ROLE_CREATE: 'role:create',
    ROLE_READ: 'role:read',
    ROLE_UPDATE: 'role:update',
    ROLE_DELETE: 'role:delete',
    ROLE_ASSIGN_PERMISSION: 'role:assign-permission',
    // Role -> sidebar-module visibility grants (RoleModule) — a separate
    // concern from ROLE_ASSIGN_PERMISSION, which still gates the
    // fine-grained CRUD permission catalog.
    ROLE_MODULE_READ: 'role-module:read',
    ROLE_MODULE_MANAGE: 'role-module:manage',
    PERMISSION_READ: 'permission:read',
    PERMISSION_MANAGE: 'permission:manage',
    USER_CREATE: 'user:create',
    USER_READ: 'user:read',
    USER_UPDATE: 'user:update',
    USER_DELETE: 'user:delete',
    USER_ASSIGN_ROLE: 'user:assign-role',
    USER_RESET_PASSWORD: 'user:reset-password',
    USER_LOCK: 'user:lock',
    USER_RESET_MFA: 'user:reset-mfa',
    USER_MFA_MANAGE: 'user:mfa-manage',
    // Administration -> Backup: a full mirror holds live secrets and patient data, so running it and
    // downloading it are separate grants.
    SYSTEM_BACKUP_READ: 'system-backup:read',
    SYSTEM_BACKUP_RUN: 'system-backup:run',
    SYSTEM_BACKUP_DOWNLOAD: 'system-backup:download',
    // Download module (admin-only Excel exports): a workbook holds patient and money records in the clear, so seeing the
    // page, building a workbook and fetching it are separate grants, exactly as for the backup above.
    DATA_EXPORT_READ: 'data-export:read',
    DATA_EXPORT_RUN: 'data-export:run',
    DATA_EXPORT_DOWNLOAD: 'data-export:download',
    LICENSE_READ: 'license:read',
    LICENSE_MANAGE: 'license:manage',
    FEATURE_READ: 'feature:read',
    FEATURE_MANAGE: 'feature:manage',
    SEQUENCE_READ: 'sequence:read',
    SEQUENCE_MANAGE: 'sequence:manage',
    FINANCIAL_YEAR_READ: 'financial-year:read',
    FINANCIAL_YEAR_MANAGE: 'financial-year:manage',
    FINANCIAL_YEAR_CLOSE: 'financial-year:close',
    ORGANIZATION_SETTINGS_READ: 'organization-settings:read',
    ORGANIZATION_SETTINGS_MANAGE: 'organization-settings:manage',
    // Enterprise Architecture Handbook v1.0, Part IV.1 (ABHA Settings).
    ABHA_SETTINGS_READ: 'abha-settings:read',
    ABHA_SETTINGS_MANAGE: 'abha-settings:manage',
    SYSTEM_SETTINGS_READ: 'system-settings:read',
    SYSTEM_SETTINGS_MANAGE: 'system-settings:manage',
    LOCALIZATION_SETTINGS_READ: 'localization-settings:read',
    LOCALIZATION_SETTINGS_MANAGE: 'localization-settings:manage',
    HOLIDAY_CREATE: 'holiday:create',
    HOLIDAY_READ: 'holiday:read',
    HOLIDAY_UPDATE: 'holiday:update',
    HOLIDAY_DELETE: 'holiday:delete',
    SHIFT_CREATE: 'shift:create',
    SHIFT_READ: 'shift:read',
    SHIFT_UPDATE: 'shift:update',
    SHIFT_DELETE: 'shift:delete',
    WORKING_HOURS_READ: 'working-hours:read',
    WORKING_HOURS_MANAGE: 'working-hours:manage',
    APPROVAL_MATRIX_READ: 'approval-matrix:read',
    APPROVAL_MATRIX_MANAGE: 'approval-matrix:manage',
    PLATFORM_BOOTSTRAP: 'platform:bootstrap',
    PLATFORM_DIAGNOSTICS: 'system:diagnostics',
    // Phase G1 — Enterprise Administration Portal's Audit Log Viewer.
    // AuditLog rows already existed (written by auditRequestMiddleware on
    // every mutating request) but had no general read path before this.
    AUDIT_LOG_READ: 'audit-log:read',
};
/** Every platform permission, flattened — used to seed the `permissions` table. */
exports.ALL_PLATFORM_PERMISSIONS = Object.values(exports.PlatformPermissions);
//# sourceMappingURL=platform.permissions.js.map